/**
 * Tacit CRM Dashboard - Main Page
 * SOTA 2026 Dark Professional Design
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { useDashboardCounts } from '@/hooks/crm/useContacts';
import { useCrmFilters } from '@/hooks/crm/useCrmFilters';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type {
  DashboardCounts,
  DashboardFilter,
  DashboardLane,
  DashboardStatsModel,
} from '@/lib/crm/dashboard';
import { CrmToolbar, LANE_LABELS } from './CrmToolbar';
import { DashboardSections } from './DashboardSections';
import { DashboardStats } from './DashboardStats';
import { TodayQueue } from './TodayQueue';

const FILTER_LABELS: Record<Exclude<DashboardFilter, null>, string> = {
  overdue: 'Overdue',
  'due-today': 'Due Today',
  'due-this-week': 'Due This Week',
  'total-active': 'Total Active',
};

function dueCountFor(stats: DashboardStatsModel, due: Exclude<DashboardFilter, null>): number {
  switch (due) {
    case 'overdue':
      return stats.overdue;
    case 'due-today':
      return stats.dueToday;
    case 'due-this-week':
      return stats.dueThisWeek;
    case 'total-active':
      return stats.totalActive;
  }
}

/**
 * "How many contacts does the current filter resolve to" — used both for the
 * toolbar's `role="status"` summary and for DashboardSections' decision to
 * render an empty state. Always derived from the index/count query, never
 * from a loaded page (see the architectural invariant in `dashboard.ts`).
 *
 * Lane and due filters are never cross-tabulated server-side (no single
 * count exists for "this lane AND this due bucket"), so when both are set
 * this favors the lane total — a documented, accepted simplification.
 */
function resolveVisibleTotal(
  counts: DashboardCounts,
  lane: DashboardLane | null,
  due: DashboardFilter,
): number {
  if (lane) return counts.laneCounts[lane];
  if (due) return dueCountFor(counts.stats, due);
  return counts.stats.totalActive;
}

function buildResultSummary(
  counts: DashboardCounts,
  lane: DashboardLane | null,
  due: DashboardFilter,
): string {
  if (lane) {
    return `Showing ${counts.laneCounts[lane]} contacts in ${LANE_LABELS[lane]}.`;
  }
  if (due) {
    return `Showing ${dueCountFor(counts.stats, due)} contacts (${FILTER_LABELS[due]}).`;
  }
  return `Showing ${counts.stats.totalActive} active contacts.`;
}

export default function CRMDashboard(): JSX.Element {
  useDocumentTitle('CRM Dashboard — Tacit CRM');

  const { filters, setFilters, searchInput, setSearchInput } = useCrmFilters();
  const { data: counts, isLoading, isError, refetch } = useDashboardCounts();

  // ONE shared snapshot, threaded down to both `TodayQueue` and
  // `DashboardSections` — they both call `useDueScheduledContacts`, which
  // keys its query on the local ISO date. If each computed its own
  // `new Date()` independently, two renders straddling local midnight could
  // disagree on which day's cached result to use, silently dropping a
  // newly-due contact from both surfaces. See `TodayQueueProps.now`'s doc
  // comment.
  const now = new Date();

  // Whether the Today "Scheduled — due now" block is showing its full
  // fetched-and-filtered list. Lifted here (rather than local state inside
  // `TodayQueue`) so `DashboardSections`'s SCHEDULED-lane exclusion set can
  // be derived from the identical boolean — see `TodayQueueProps`'s doc
  // comment for the drift this closes.
  const [dueScheduledExpanded, setDueScheduledExpanded] = useState(false);

  const handleDueFilterChange = (filter: Exclude<DashboardFilter, null>): void => {
    setFilters({ due: filters.due === filter ? null : filter });
  };

  const handleLaneChange = (lane: DashboardLane | null): void => {
    setFilters({ lane });
  };

  const handleClearAllFilters = (): void => {
    setSearchInput('');
    setFilters({ lane: null, due: null, page: 1 });
  };

  const handlePageChange = (page: number): void => {
    setFilters({ page });
  };

  // Drives the single clear-everything affordance in `CrmToolbar` — it only
  // makes sense to offer "Clear filters" once one of the three filters it
  // resets (search, lane, due) is actually active. `searchInput` (not
  // `filters.q`) is used so the button appears the instant the operator
  // starts typing, before the debounce commits the value to the URL.
  const hasActiveFilters = searchInput !== '' || filters.lane !== null || filters.due !== null;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl bg-background p-4 sm:p-6 lg:p-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">CRM Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review relationship status and the next useful action.
          </p>
        </header>

        {isLoading && (
          <div className="py-12 text-center" role="status" aria-live="polite">
            <div
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"
              aria-hidden="true"
            />
            <p className="mt-4 text-muted-foreground">Loading contacts...</p>
          </div>
        )}

        {!isLoading && isError && (
          <div
            className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-10 text-center"
            role="alert"
          >
            <h2 className="text-lg font-semibold">Contacts could not be loaded.</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Check your connection and try again. No contact data was changed.
            </p>
            <Button
              variant="outline"
              className="mt-4 min-h-11 md:min-h-11"
              onClick={() => void refetch()}
            >
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && counts && (
          <>
            <TodayQueue
              now={now}
              dueScheduledExpanded={dueScheduledExpanded}
              onToggleDueScheduledExpanded={() => setDueScheduledExpanded((expanded) => !expanded)}
            />

            <DashboardStats
              overdueCount={counts.stats.overdue}
              dueTodayCount={counts.stats.dueToday}
              dueThisWeekCount={counts.stats.dueThisWeek}
              totalActiveCount={counts.stats.totalActive}
              activeFilter={filters.due}
              onFilterChange={handleDueFilterChange}
            />

            {filters.due && (
              <div className="mb-6 flex flex-wrap items-center gap-2" role="status">
                <span className="text-sm text-muted-foreground">
                  Showing: {FILTER_LABELS[filters.due]}
                </span>
              </div>
            )}

            <CrmToolbar
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
              lane={filters.lane}
              onLaneChange={handleLaneChange}
              onClearFilters={handleClearAllFilters}
              hasActiveFilters={hasActiveFilters}
              resultSummary={buildResultSummary(counts, filters.lane, filters.due)}
            />

            <DashboardSections
              filters={filters}
              counts={counts}
              visibleTotal={resolveVisibleTotal(counts, filters.lane, filters.due)}
              onPageChange={handlePageChange}
              now={now}
              dueScheduledExpanded={dueScheduledExpanded}
            />
          </>
        )}
      </div>
    </Layout>
  );
}
